import { defineStore } from 'pinia';

interface GeolocationState {
  uuid: string | null;
  manualMethod: string;
  address: string;
  zipCode: string;
  city: string;
  country_code: string;
  latitude: string | number | null;
  longitude: string | number | null;
  formattedAddress: string;
  isEditMode: boolean;
  disableAutoSearch: boolean;
}

export const useGeolocationStore = defineStore('geolocation', {
  state: (): GeolocationState => ({
    uuid: null,
    manualMethod: 'address',
    address: '',
    zipCode: '',
    city: '',
    country_code: 'FR',
    latitude: null,
    longitude: null,
    formattedAddress: '',
    isEditMode: false,
    disableAutoSearch: false
  }),

  actions: {
    // Activer/désactiver le mode édition
    setEditMode(value: boolean) {
      this.isEditMode = value;
      // En mode édition, désactiver automatiquement la recherche auto
      if (value) {
        this.disableAutoSearch = true;
      }
    },

    // Activer/désactiver explicitement la recherche automatique
    setAutoSearch(enabled: boolean) {
      this.disableAutoSearch = !enabled;
    },

    initGeolocationForm(geolocation: Partial<GeolocationState>) {
      // Si on a un UUID, on est en mode édition
      if (geolocation.uuid) {
        this.setEditMode(true);
      } else {
        this.setEditMode(false);
        this.disableAutoSearch = false;
      }

      this.uuid = geolocation.uuid || null;
      this.manualMethod = geolocation.manualMethod || 'address';
      this.address = geolocation.address || '';
      this.zipCode = geolocation.zipCode || '';
      this.city = geolocation.city || '';
      this.country_code = geolocation.country_code || 'FR';
      this.latitude = geolocation.latitude || null;
      this.longitude = geolocation.longitude || null;
      this.formattedAddress = geolocation.formattedAddress || '';
    },

    updateField(field: keyof GeolocationState, value: any) {
      // Utiliser une assertion de type pour indiquer à TypeScript 
      // que cette opération est sûre
      (this as any)[field] = value;

      // Ne pas déclencher de recherche si on est en mode édition
      // ou si la recherche automatique est désactivée
      if (!this.isEditMode && !this.disableAutoSearch) {
        if (field === 'address' || field === 'zipCode' || field === 'city' || field === 'country_code') {
          // Code pour déclencher la recherche pourrait être ajouté ici
        }
      }
    },

    resetForm() {
      this.uuid = null;
      this.manualMethod = 'address';
      this.address = '';
      this.zipCode = '';
      this.city = '';
      this.country_code = 'FR';
      this.latitude = null;
      this.longitude = null;
      this.formattedAddress = '';
      this.isEditMode = false;
      this.disableAutoSearch = false;
    },

    createPayload() {
      const latitude = this.latitude ? Number(this.latitude) : null;
      const longitude = this.longitude ? Number(this.longitude) : null;

      // S'assurer que le pays est un code et non un nom
      let country_code = this.country_code;
      if (country_code === 'France') country_code = 'FR';

      return {
        manualMethod: this.manualMethod,
        address: this.address,
        zipCode: this.zipCode,
        city: this.city,
        country_code: country_code,
        formattedAddress: this.formattedAddress || 
          `${this.address} ${this.zipCode} ${this.city}`.trim(),
        latitude,
        longitude
      };
    }
  }
});
